import { useEffect, useState } from "react";
import { Plus, Trash2, Edit, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageTitle, Card, Field, inputCls, SaveButton } from "@/components/admin/ui";
import { ImageField } from "@/components/admin/MediaPicker";
import { DynIcon } from "@/components/site/Icon";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ICONS = ["Scale", "ShieldAlert", "Users", "Home", "ShoppingBag", "FileSignature", "Handshake", "Landmark", "BookText", "FileText", "Mail", "MessageSquare", "Gavel", "Briefcase", "Building2", "CreditCard", "Car", "Globe", "Lock"];
const empty = { title: "", icon: "Scale", short_desc: "", long_desc: "", services: [], image_url: "", order: 0, published: true };

export default function AdminPracticeAreas() {
  const [areas, setAreas] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/practice-areas?all=true").then((r) => setAreas(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...empty, order: areas.length });
  const save = async () => {
    if (!editing.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const payload = { ...editing, services: (editing.services || []).filter(Boolean) };
      if (editing.id) await api.put(`/practice-areas/${editing.id}`, payload);
      else await api.post("/practice-areas", payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete this practice area?")) return; await api.delete(`/practice-areas/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="admin-practice-areas">
      <PageTitle title="Practice Areas" subtitle="Manage the legal services shown on the website">
        <button onClick={openNew} className="bg-navy text-ivory px-4 py-2.5 rounded-sm text-sm inline-flex items-center gap-2 hover:bg-navy-light" data-testid="add-practice-area"><Plus size={16} /> Add Practice Area</button>
      </PageTitle>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {areas.map((a) => (
          <div key={a.id} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`admin-area-${a.slug}`}>
            <div className="flex items-start justify-between mb-3">
              <span className="w-10 h-10 rounded-sm bg-navy text-brass flex items-center justify-center"><DynIcon name={a.icon} size={18} /></span>
              <div className="flex gap-1">
                <button onClick={() => setEditing({ ...a })} className="p-2 text-slate-500 hover:text-navy" data-testid={`edit-area-${a.slug}`}><Edit size={15} /></button>
                <button onClick={() => del(a.id)} className="p-2 text-slate-500 hover:text-red-500"><Trash2 size={15} /></button>
              </div>
            </div>
            <h3 className="font-serif text-navy text-base mb-1">{a.title}</h3>
            <p className="text-slate-400 text-xs line-clamp-2">{a.short_desc}</p>
            {!a.published && <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full mt-2 inline-block">Hidden</span>}
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} Practice Area</DialogTitle></DialogHeader>
          {editing && (
            <div>
              <Field label="Title"><input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="area-title-input" /></Field>
              <Field label="Icon">
                <div className="flex flex-wrap gap-2">
                  {ICONS.map((ic) => (
                    <button key={ic} onClick={() => setEditing({ ...editing, icon: ic })} className={`w-10 h-10 rounded-sm flex items-center justify-center border ${editing.icon === ic ? "bg-navy text-brass border-navy" : "border-slate-200 text-slate-500"}`}><DynIcon name={ic} size={17} /></button>
                  ))}
                </div>
              </Field>
              <Field label="Short Description"><textarea rows={2} className={inputCls} value={editing.short_desc} onChange={(e) => setEditing({ ...editing, short_desc: e.target.value })} /></Field>
              <Field label="Long Description"><textarea rows={4} className={inputCls} value={editing.long_desc} onChange={(e) => setEditing({ ...editing, long_desc: e.target.value })} /></Field>
              <Field label="Services (one per line)"><textarea rows={5} className={inputCls} value={(editing.services || []).join("\n")} onChange={(e) => setEditing({ ...editing, services: e.target.value.split("\n") })} /></Field>
              <Field label="Image (optional)"><ImageField value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} /></Field>
              <Field label="Display Order"><input type="number" className={inputCls} value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value) || 0 })} /></Field>
              <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="accent-[#C5A059]" /> Published (visible on website)</label>
              <SaveButton onClick={save} saving={saving} testid="save-area" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
